import { onScopeDispose, watch } from 'vue';

import { useAuthStore } from '@/entities/auth';
import {
  createDataRefreshCoordinator,
  createDataRefreshLifecycle,
  provideDataRefreshCoordinator,
  type DataRefreshCoordinator,
  type DataRefreshOutcome,
} from '@/features/data-refresh';
import { apolloOperationGate } from '@/shared/api';
import { useToast } from '@/shared/lib';

const isOnline = (): boolean => navigator.onLine !== false;

export const useDataRefreshRuntime = (): DataRefreshCoordinator => {
  const authStore = useAuthStore();
  const toast = useToast();
  const isReady = (): boolean => {
    return (
      authStore.isAuthenticated &&
      authStore.isContextChecked &&
      authStore.currentOrganization !== null &&
      !apolloOperationGate.isRunning('tenant-transition')
    );
  };
  const notifyManualIssue = (outcome: DataRefreshOutcome): void => {
    if (outcome.kind === 'offline') {
      toast.error('Нет подключения к интернету');
      return;
    }

    toast.error('Не удалось обновить данные. Попробуйте ещё раз');
  };
  const coordinator = createDataRefreshCoordinator(undefined, {
    isOnline,
    canRefresh: isReady,
    runExclusive: (operation) => apolloOperationGate.runExclusive('data-refresh', operation),
    notifyManualIssue,
  });
  const lifecycle = createDataRefreshLifecycle({
    refresh: coordinator.refresh,
    isReady,
    isOnline,
    isVisible: () => document.visibilityState === 'visible',
  });

  provideDataRefreshCoordinator(coordinator);

  const handleVisibilityChange = (): void => {
    void lifecycle.handleVisibilityChange(document.visibilityState === 'visible');
  };
  const handleOnline = (): void => {
    void lifecycle.handleOnline();
  };
  const handleOffline = (): void => {
    lifecycle.handleOffline();
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  const stopReadinessWatch = watch(isReady, (ready) => {
    lifecycle.handleReadinessChange(ready);
  });

  onScopeDispose(() => {
    stopReadinessWatch();
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
    lifecycle.dispose();
    coordinator.dispose();
  });

  return coordinator;
};
